import {
  Cat20TokenInfo,
  Cat721Metadata,
  Cat721NftInfo,
  Cat721Utxo,
  MempolChainProvider,
  MempoolUtxoProvider,
  OpenMinterCat20Meta,
  SupportedNetwork,
  bitcoinjs,
} from "@cat-protocol/cat-sdk";
import axios from "axios";
import { AxiosResponse } from "axios";
import { getAllNfts } from "./apis-tracker";
import { UTXO } from "scrypt-ts";

function getBackendServiceUrl(network: SupportedNetwork): string {
  const toUpperCase = network.toUpperCase().replaceAll("-", "_");
  const BACKEND_SERVICE_URL =
    process.env[`REACT_APP_${toUpperCase}_BACKEND_SERVICE_URL`];
  if (!BACKEND_SERVICE_URL)
    throw new Error(
      `REACT_APP_${toUpperCase}_BACKEND_SERVICE_URL should be set in env`
    );
  return BACKEND_SERVICE_URL.replace(/\/$/, "");
}

export function getExplorerServiceUrl(network: SupportedNetwork): string {
  const toUpperCase = network.toUpperCase().replaceAll("-", "_");
  const EXPLORER_SERVICE_URL =
    process.env[`REACT_APP_${toUpperCase}_EXPLORER_SERVICE_URL`];
  if (!EXPLORER_SERVICE_URL)
    throw new Error(
      `REACT_APP_${toUpperCase}_EXPLORER_SERVICE_URL should be set in env`
    );
  return EXPLORER_SERVICE_URL.replace(/\/$/, "");
}

export function getTrackerUrl(v1: boolean, network: SupportedNetwork): string {
  const _network = network.replaceAll("fractal-", "").toUpperCase();
  const prefix = v1 ? "TRACKER_V1" : "TRACKER_V2";
  const trackerUrl = process.env[`REACT_APP_${prefix}_${_network}_URL`];
  if (!trackerUrl)
    throw new Error(`REACT_APP_${prefix}_${_network}_URL should be set in env`);
  return trackerUrl.replace(/\/$/, "");
}

export interface FeeRateLevels {
  minimumFee: number;
  economyFee: number;
  hourFee: number;
  halfHourFee: number;
  fastestFee: number;
}

export interface ChainStats {
  blockNumber: number;
  btcInUSD: number;
  feeRateLevels: FeeRateLevels;
}

export type TokenItem = Cat20TokenInfo<OpenMinterCat20Meta> & {
  token2Id: string;
  confirmed: string;
  token2Confirmed: string;
  symbol: string;
  paymentAddr: string;
};

export type CollectionItem = Cat721NftInfo<Cat721Metadata> & {
  collection2Id: string;
  confirmed: string;
  collection2Confirmed: string;
  symbol: string;
  paymentAddr: string;
};

export interface NFTItem {
  localId: bigint;
  collectionId: string;
  url: string;

  utxo: Cat721Utxo;
}

export const getFeeRate = async function (
  network: SupportedNetwork
): Promise<number> {
  const provider = new MempolChainProvider(network);
  const url = `${provider.getMempoolApiHost()}/api/v1/fees/recommended`;
  return axios
    .get<any, AxiosResponse<number, any>>(url)
    .then((resp: any) => {
      if (resp.status === 200) {
        return Math.max(2, resp.data["fastestFee"] || 1);
      } else {
        return 1;
      }
    })
    .catch((e: any) => {
      console.error(`fetch feeRate failed:`, e);
      return 1;
    });
};

export function getChainStats(network: SupportedNetwork) {
  return axios
    .get<any, AxiosResponse<ChainStats, any>>(
      `${getBackendServiceUrl(network)}/api/chainstats`
    )
    .then((resp) => {
      if (resp.status === 200) {
        console.log("chainStats", resp.data);
        return resp.data;
      } else {
        throw new Error("invalid http status: " + resp.statusText);
      }
    });
}

export function getTokenItems(
  network: SupportedNetwork,
  address: string,
  nonupgradable: boolean = false
) {
  return axios
    .get<any, AxiosResponse<TokenItem[], any>>(
      `${getBackendServiceUrl(network)}/tokens/${address}/${
        nonupgradable ? "nonupgradable" : "balances"
      }`
    )
    .then((resp) => {
      if (resp.status === 200) {
        return resp.data.map((item) => ({
          ...item,
          metadata: (item as any)["info"],
        }));
      } else {
        throw new Error("invalid http status: " + resp.statusText);
      }
    })
    .catch((e: Error) => {
      console.error("getTokenItems error:", e);
      return [];
    });
}

export function getCollectionItems(
  network: SupportedNetwork,
  address: string,
  nonupgradable: boolean = false
) {
  return axios
    .get<any, AxiosResponse<CollectionItem[], any>>(
      `${getBackendServiceUrl(network)}/collections/${address}/${
        nonupgradable ? "nonupgradable" : "balances"
      }`
    )
    .then((resp) => {
      if (resp.status === 200) {
        return resp.data.map((item) => ({
          ...item,
        }));
      } else {
        throw new Error("invalid http status: " + resp.statusText);
      }
    })
    .catch((e: Error) => {
      console.error("getCollectionItems error:", e);
      return [];
    });
}

export function getNFTItems(
  network: SupportedNetwork,
  address: string,
  collectionId: string
): Promise<NFTItem[]> {
  return getAllNfts(true, network, address, collectionId).then((utxos) => {
    const baseApi = getTrackerUrl(true, network);
    return utxos.map((utxo) => {
      const url = `${baseApi}/api/collections/${collectionId}/localId/${utxo.state.localId}/content`;
      return {
        localId: utxo.state.localId,
        collectionId: collectionId,
        url,
        utxo,
      };
    });
  });
}

export enum TxStatus {
  initial = "initial",
  pending = "pending",
  invalid = "unknown",

  finished = "finished",

  failed = "failed",
}

export function getTxStatus(
  txId: string,
  network: SupportedNetwork
): Promise<TxStatus> {
  return axios
    .get<
      any,
      AxiosResponse<
        {
          state: TxStatus;
        },
        any
      >
    >(`${getBackendServiceUrl(network)}/tokens/tx/${txId}`)
    .then((resp) => {
      return resp.data.state;
    })
    .catch((e) => {
      console.error("getTxStatus error:", e);
      return TxStatus.invalid;
    });
}

export const boardcast = async (
  txHex: string,
  userAddr: string,
  tokenId: string,
  network: SupportedNetwork
): Promise<any> => {
  if (localStorage.getItem("debug") === "1") {
    console.log("boardcast", userAddr, txHex);
    return bitcoinjs.Transaction.fromHex(txHex).getId();
  }

  const response: AxiosResponse<{
    txid: string;
  }> = await axios.post(
    `${getBackendServiceUrl(network)}/tokens/tx`,
    {
      txHex,
      userAddr,
      tokenId,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export function getUtxos(
  address: string,
  network: SupportedNetwork
): Promise<UTXO[]> {
  return axios
    .get<any, AxiosResponse<UTXO[], any>>(
      `${getBackendServiceUrl(network)}/address/${address}/utxos`
    )
    .then((resp) => {
      return resp.data;
    })
    .catch((e) => {
      return [];
    });
}

export function patchGetUtxos(provider: MempoolUtxoProvider) {
  provider.getUtxos = (address, options) => {
    return getUtxos(address, provider.network);
  };
}
